import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { responseError } from '@/lib/api-error'
import { hasActiveContextFilters } from './search-filters'
import type {
  MultiSelections,
  ProductSort,
  RangeSelections,
  ShopifyFilterOptionsResponse,
  ShopifySearchResponse,
} from './search-state'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'
const FILTER_OPTION_LIMIT = 18
const SPEC_FILTER_LIMIT = 24

function appendSelections(
  params: URLSearchParams,
  multiSelections: MultiSelections,
  rangeSelections: RangeSelections,
) {
  Object.entries(multiSelections).forEach(([key, values]) => {
    if (!values.length) return
    if (key.startsWith('spec.')) {
      const slug = key.slice('spec.'.length)
      const firstValue = values[0]
      if (values.length === 1 && (firstValue === 'true' || firstValue === 'false')) {
        params.append(`spec_bool[${slug}]`, firstValue)
      } else {
        values.forEach((value) => params.append(`spec[${slug}]`, value))
      }
      return
    }
    values.forEach((value) => params.append(key, value))
  })

  Object.entries(rangeSelections).forEach(([key, range]) => {
    if (key === 'price') {
      if (range.min) params.set('price_min', range.min)
      if (range.max) params.set('price_max', range.max)
      return
    }
    if (key.startsWith('spec.')) {
      const slug = key.slice('spec.'.length)
      if (range.min) params.set(`spec_range[${slug}][min]`, range.min)
      if (range.max) params.set(`spec_range[${slug}][max]`, range.max)
    }
  })
}

export function buildFilterParams(
  q: string,
  multiSelections: MultiSelections,
  rangeSelections: RangeSelections,
) {
  const params = new URLSearchParams()
  const trimmed = q.trim()
  if (trimmed) params.set('q', trimmed)
  params.set('option_limit', String(FILTER_OPTION_LIMIT))
  params.set('spec_limit', String(SPEC_FILTER_LIMIT))
  appendSelections(params, multiSelections, rangeSelections)
  return params
}

export function buildSearchParams(
  q: string,
  multiSelections: MultiSelections,
  rangeSelections: RangeSelections,
  page: number,
  perPage: number,
  sort: ProductSort,
) {
  const params = new URLSearchParams()
  params.set('q', q.trim())
  params.set('page', String(Math.max(1, page)))
  params.set('per_page', String(perPage))
  params.set('products_limit', String(perPage))
  params.set('vendors_limit', '0')
  params.set('categories_limit', '0')
  params.set('sort', sort)
  appendSelections(params, multiSelections, rangeSelections)
  return params
}

// Public Shopify endpoints deliberately use plain fetch, not the dashboard auth client.
export async function fetchJson<T>(path: string, params: URLSearchParams): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}?${params.toString()}`)
  if (!response.ok) {
    throw await responseError(response, `Request failed with ${response.status}`)
  }
  return response.json() as Promise<T>
}

export function useShopifyFilterOptions(
  q: string,
  multiSelections: MultiSelections,
  rangeSelections: RangeSelections,
) {
  const queryParams = useMemo(
    () => buildFilterParams(q, multiSelections, rangeSelections).toString(),
    [q, multiSelections, rangeSelections],
  )
  return useQuery({
    queryKey: ['shopify-filter-options', queryParams],
    queryFn: () =>
      fetchJson<ShopifyFilterOptionsResponse>(
        '/shopify/filter-options',
        new URLSearchParams(queryParams),
      ),
    staleTime: 60_000,
    placeholderData: (previousData) => previousData,
  })
}

export function useShopifySearch(
  q: string,
  multiSelections: MultiSelections,
  rangeSelections: RangeSelections,
  page: number,
  perPage: number,
  sort: ProductSort,
) {
  const queryParams = useMemo(
    () => buildSearchParams(q, multiSelections, rangeSelections, page, perPage, sort).toString(),
    [q, multiSelections, rangeSelections, page, perPage, sort],
  )
  return useQuery({
    queryKey: ['shopify-public-search', queryParams],
    queryFn: () =>
      fetchJson<ShopifySearchResponse>('/shopify/search', new URLSearchParams(queryParams)),
    enabled: Boolean(q.trim() || hasActiveContextFilters(multiSelections, rangeSelections)),
    staleTime: 30_000,
  })
}
